import React from 'react'
import type { PresentationBlock, QuoteFile } from 'paintscout'

import { ButtonOption, ButtonSelectOption } from '../../components'
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined'
import OpenWithIcon from '@material-ui/icons/OpenWith'
import { getSliderAnchorVisibility } from './getSliderAnchorVisibility'

interface SliderImageOptionsProps {
  block: PresentationBlock
  presetColors: string[]
  handleOptionFieldChange: (field: string, value: any) => void
  /** Opens a single-file replace dialog — see PresentationInspector.tsx. */
  openReplaceImage?: (args: { file: QuoteFile; onSelect: (file: QuoteFile) => void }) => void
}

const ASPECT_RATIO_BUTTONS = [
  { icon: <OpenWithIcon />, value: 'unset' },
  { icon: '1:1', value: '1/1' },
  { icon: '5:4', value: '5/4' },
  { icon: '4:3', value: '4/3' },
  { icon: '3:2', value: '3/2' },
  { icon: '16:9', value: '16/9' },
  { icon: '3:1', value: '3/1' }
]

const POSITION_BUTTONS = [
  { icon: 0, value: 0 },
  { icon: 25, value: 25 },
  { icon: 50, value: 50 },
  { icon: 75, value: 75 },
  { icon: 100, value: 100 }
]

// The images are no longer editable inline on the canvas (the slider is always shown, and is
// only draggable while focused — #24097 follow-up), so replacing either side moved here,
// mirroring Banner's poster-image "Replace Image" (BannerOptions.tsx).
function SliderImageOptions(props: SliderImageOptionsProps) {
  const { block, handleOptionFieldChange, openReplaceImage } = props
  const { fields = {} } = block
  const leftImage: QuoteFile = fields.leftImage || {}
  const rightImage: QuoteFile = fields.rightImage || {}
  const leftPresentationOptions = leftImage.presentationOptions ?? {}
  const rightPresentationOptions = rightImage.presentationOptions ?? {}

  // Aspect ratio and crop position apply to the pair as a single combined slider (there's one
  // shared height, per getSliderAspectRatio.ts), so both images are kept in sync rather than
  // exposing per-image controls — matches inspectorOptions.tsx's media Aspect Ratio/Anchor
  // Position controls, just applied to both sides of the slider at once.
  const aspectRatio = leftPresentationOptions.aspectRatio ?? rightPresentationOptions.aspectRatio ?? 'unset'
  const objectPositionX = leftPresentationOptions.objectPositionX ?? rightPresentationOptions.objectPositionX ?? 50
  const objectPositionY = leftPresentationOptions.objectPositionY ?? rightPresentationOptions.objectPositionY ?? 50

  // Only show the axis that a crop actually moves — see getSliderAnchorVisibility for the ratio
  // math, matching inspectorOptions.tsx's media Anchor Position gating (landscape-source branch
  // only; there's no orientation control for the slider).
  const referenceImage = leftImage.width ? leftImage : rightImage
  const { hasXAnchor, hasYAnchor } = getSliderAnchorVisibility({
    aspectRatio: String(aspectRatio),
    width: referenceImage.width,
    height: referenceImage.height
  })

  const updateBothImages = (key: string, value: string | number | boolean) => {
    // only slots that hold a real image — writing `{ presentationOptions }` into an empty slot
    // makes it truthy without a `src` (#24160)
    if (leftImage.src) {
      handleOptionFieldChange('fields.leftImage', {
        ...leftImage,
        presentationOptions: { ...leftPresentationOptions, [key]: value }
      })
    }
    if (rightImage.src) {
      handleOptionFieldChange('fields.rightImage', {
        ...rightImage,
        presentationOptions: { ...rightPresentationOptions, [key]: value }
      })
    }
  }

  return [
    {
      Component: (
        <ButtonOption
          label={'Before Image'}
          buttons={[
            {
              icon: <PhotoLibraryOutlinedIcon />,
              onClick: () =>
                openReplaceImage?.({
                  file: leftImage,
                  onSelect: (newFile) => handleOptionFieldChange('fields.leftImage', newFile)
                })
            }
          ]}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonOption
          label={'After Image'}
          buttons={[
            {
              icon: <PhotoLibraryOutlinedIcon />,
              onClick: () =>
                openReplaceImage?.({
                  file: rightImage,
                  onSelect: (newFile) => handleOptionFieldChange('fields.rightImage', newFile)
                })
            }
          ]}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Aspect Ratio'}
          value={aspectRatio}
          buttons={ASPECT_RATIO_BUTTONS}
          onChange={(value) => updateBothImages('aspectRatio', value)}
        />
      ),
      tab: 'format'
    },
    ...(aspectRatio !== 'unset' && hasXAnchor
      ? [
          {
            Component: (
              <ButtonSelectOption
                label={'Anchor Position (X)'}
                value={objectPositionX}
                buttons={POSITION_BUTTONS}
                onChange={(value) => updateBothImages('objectPositionX', value)}
              />
            ),
            tab: 'format'
          }
        ]
      : []),
    ...(aspectRatio !== 'unset' && hasYAnchor
      ? [
          {
            Component: (
              <ButtonSelectOption
                label={'Anchor Position (Y)'}
                value={objectPositionY}
                buttons={POSITION_BUTTONS}
                onChange={(value) => updateBothImages('objectPositionY', value)}
              />
            ),
            tab: 'format'
          }
        ]
      : [])
  ]
}

export default SliderImageOptions
